import type {
  CouponDiscount,
  InvoiceEstimate,
} from "features/subscriptions/types";

import { trackMetaPixel } from "common/helpers/utils";
import type { Pricing, PricingPeriodInfo } from "common/types";

export function trackSubscriptionEvent(
  type: "purchase" | "subscribe",
  subscriptionId: string,
  value: number,
  quantity: number,
) {
  const eventData = {
    value: value / 100,
    currency: "USD",
    content_type: "subscription",
    content_ids: [subscriptionId],
    num_items: quantity,
  };

  if (type === "purchase") {
    trackMetaPixel("track", "Purchase", eventData);
  } else {
    trackMetaPixel("track", "Subscribe", eventData);
  }
}

export function formatPrice(price: number) {
  return Math.max(0, price / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    trailingZeroDisplay: "stripIfInteger",
  });
}

export function formatPricingPeriodInfo(pricingPeriodInfo: PricingPeriodInfo) {
  const { period, period_unit } = pricingPeriodInfo;

  if (period === 1) {
    return period_unit;
  }
  return `${period} ${period_unit}s`;
}

export function parseEstimate(invoiceEstimate: InvoiceEstimate) {
  const flatFeeItem = invoiceEstimate.line_items.find(
    ({ pricing_model }: { pricing_model: string }) =>
      pricing_model === "flat_fee",
  );
  const perUnitLineItem = invoiceEstimate.line_items.find(
    ({ pricing_model }: { pricing_model: string }) =>
      pricing_model === "per_unit",
  );

  let creditsApplied = 0;
  const amountDue = invoiceEstimate.amount_due;
  let coupon: CouponDiscount;

  if (invoiceEstimate.discounts) {
    const promotionalCreditDiscount = invoiceEstimate.discounts.find(
      ({ entity_type }) => entity_type === "promotional_credits",
    );
    const proratedCreditDiscount = invoiceEstimate.discounts.find(
      ({ entity_type }) => entity_type === "prorated_credits",
    );
    const couponDiscount = invoiceEstimate.discounts.find(
      ({ entity_type }) =>
        entity_type === "item_level_coupon" ||
        entity_type === "document_level_coupon",
    ) as CouponDiscount;

    if (couponDiscount) {
      coupon = couponDiscount;
    }
    if (promotionalCreditDiscount) {
      creditsApplied += promotionalCreditDiscount.amount;
    }
    if (proratedCreditDiscount) {
      creditsApplied += proratedCreditDiscount.amount;
    }
  }

  return {
    amountDue,
    pricePerItem: perUnitLineItem ? perUnitLineItem.unit_amount : 0,
    //when there is no change, the flat fee item is not returned by chargebee, maybe we shouldn't call the request at all
    basePrice: flatFeeItem ? flatFeeItem.unit_amount : 0,
    creditsApplied: creditsApplied,
    coupon,
  };
}

export function getPricingPeriodValue(pricing: Pricing) {
  return (pricing.period_unit === "month" ? 30 : 360) * pricing.period;
}

//returns either a percentage or $ amount
export function getCouponDiscountString(
  coupon: ReturnType<typeof parseEstimate>["coupon"],
) {
  const { discount_type } = coupon;
  const discountAmountString =
    discount_type === "percentage"
      ? `${coupon.discount_percentage}%`
      : `${formatPrice(coupon.discount_amount)}`;

  let discountDurationString = "forever";

  if (coupon.duration_type === "limited_period") {
    discountDurationString = `for ${coupon.period} ${coupon.period_unit}${coupon.period > 1 ? "s" : ""}`;
  } else if (coupon.duration_type === "one_time") {
    discountDurationString = "for one time";
  }

  return `${discountAmountString} OFF ${discountDurationString}`;
}

export function getCouponUpdateState(
  couponAction: string | "remove" | undefined,
) {
  if (couponAction) {
    return couponAction === "remove"
      ? { removeCoupon: true }
      : { removeCoupon: true, coupon: couponAction };
  }
}
